import React, { useState, useMemo, useEffect } from "react";
import { useApplication } from "@/contexts/ApplicationContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import DateTimeSelector from "./DateTimeSelector";
import { useSessionDates } from "@/hooks/useSessionDates";
import { useOrientationCloudFunctions } from "@/hooks/useOrientationCloudFunctions";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";

const Orientation: React.FC = () => {
  const { application, refetch, setApplication } = useApplication();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Use the orientation cloud function
  const { scheduleOrientation } = useOrientationCloudFunctions();

  // Fetch available session dates using our custom hook
  const {
    data: sessionDates = [],
    isLoading: isLoadingDates,
    error: datesError,
  } = useSessionDates(application?.jobId);

  // Pre-select date if already set in application
  useEffect(() => {
    if (application?.orientation?.date && sessionDates.length > 0) {
      // Parse the stored date string to a Date object
      const orientationDate = new Date(application.orientation.date);

      // Find the matching session date from available dates
      const matchingSession = sessionDates.find((session) => {
        const sessionDate = session.date;
        return (
          sessionDate.getFullYear() === orientationDate.getFullYear() &&
          sessionDate.getMonth() === orientationDate.getMonth() &&
          sessionDate.getDate() === orientationDate.getDate() &&
          sessionDate.getHours() === orientationDate.getHours() &&
          sessionDate.getMinutes() === orientationDate.getMinutes()
        );
      });

      // If we found a matching session, select it
      if (matchingSession) {
        setSelectedDate(matchingSession.date);
      }
    }
  }, [application?.orientation?.date, sessionDates]);

  // Convert session dates to Date objects for the DateTimeSelector component
  const availableDates = useMemo(() => {
    return sessionDates.map((session) => session.date);
  }, [sessionDates]);

  const availableTimesForDate = (date: Date) => {
    if (!date) return [];
    return availableDates.filter(
      (availableDate) =>
        availableDate.getDate() === date.getDate() &&
        availableDate.getMonth() === date.getMonth() &&
        availableDate.getFullYear() === date.getFullYear()
    );
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (fullDate: Date) => {
    setSelectedDate(fullDate);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !application?.applicationId) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time for your orientation",
        variant: "destructive",
      });
      return;
    }

    // Find the selected session to get its ID
    const selectedSession = sessionDates.find(
      (session) => session.date.getTime() === selectedDate.getTime()
    );

    if (!selectedSession) {
      toast({
        title: "Invalid Selection",
        description: "The selected time slot is no longer available",
        variant: "destructive",
      });
      return;
    }

    try {
      // Schedule orientation using cloud function
      const result = await scheduleOrientation.mutateAsync({
        applicationId: application.applicationId!,
        sessionId: selectedSession.sessionId,
        date: selectedDate,
      });

      if (!result.success) {
        Sentry.captureException(result);
        // Check if the error is about already attended orientation
        if (result.message?.includes("already attended")) {
          toast({
            title: "Already Attended",
            description: result.message,
            variant: "destructive",
          });
          return; // Return early to prevent further processing
        } else {
          throw new Error(result.message || "Failed to schedule orientation");
        }
      }

      // Refetch application data to ensure it's up to date
      await refetch();

      // Save depot information to application context
      if (selectedSession.depot && application) {
        setApplication({
          ...application,
          orientation: {
            ...application.orientation,
            date: selectedDate.toISOString(),
            depot: {
              name: selectedSession.depot.name,
              address: selectedSession.depot.address,
              city: selectedSession.depot.city,
            },
          },
        });
      }

      // Show success message
      toast({
        title: "Orientation Scheduled",
        description: `Your orientation is scheduled for ${format(
          selectedDate,
          "EEEE, MMMM d, yyyy"
        )} at ${format(selectedDate, "h:mm a")}`,
      });

      // Navigate to the next step
      navigate("/orientation-confirmation");
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "Orientation.handleSubmit",
          applicationId: application?.id,
          selectedDate,
          selectedSession,
        },
      });
      console.error("Error booking orientation session:", error);
      toast({
        title: "Booking Failed",
        description:
          "There was an error booking your orientation. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Schedule Your Orientation</CardTitle>
          <CardDescription>
            Select a date and time for your in-person orientation session at our
            facility
          </CardDescription>
        </CardHeader>
        <CardContent>
          {datesError && (
            <div className="bg-red-50 text-red-700 p-4 mb-4 rounded-md">
              Error loading available dates. Please try again later.
            </div>
          )}

          {isLoadingDates && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-honeycomb mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading available dates...</p>
            </div>
          )}
          {!isLoadingDates && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <DateTimeSelector
                availableDates={availableDates}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                onTimeSelect={handleTimeSelect}
                title="Your Orientation Session"
                locationText={
                  // Find the selected session to get its depot information
                  (() => {
                    const selectedSession = sessionDates.find(
                      (session) =>
                        selectedDate &&
                        session.date.getTime() === selectedDate.getTime()
                    );
                    if (selectedSession?.depot) {
                      return `${selectedSession.depot.name}, ${selectedSession.depot.address}, ${selectedSession.depot.city}`;
                    }
                    return "Please select a date and time";
                  })()
                }
              />

              {/* Important Notes */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-3">Important Notes:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-honeycomb rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Please arrive 15 minutes before your scheduled time
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-honeycomb rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Bring your driving license and right to work documents
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-honeycomb rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    The orientation session will last approximately 2 hours
                  </li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full bg-honeycomb hover:bg-amber-500 text-white"
                disabled={
                  !selectedDate ||
                  !availableTimesForDate(selectedDate).some(
                    (t) => t.getTime() === selectedDate.getTime()
                  ) ||
                  scheduleOrientation.isPending
                }
              >
                {scheduleOrientation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Booking...
                  </>
                ) : (
                  "Confirm Orientation"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Orientation;
